import angular, { auto as ng, IScope } from 'angular';
import React from 'react';

import getAngularInjector from '@react-bridge/getAngularInjector';

export type NgWrapperDivProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

/**
 * Abstract base class for creating a React Component that calls an Angular component.
 */
type WithWrapperProps<T> = T & {
  ngWrapperDivProps?: NgWrapperDivProps;
};
export default abstract class ReactAngularWidget<
  P extends { ngWrapperDivProps?: NgWrapperDivProps } = Record<string, unknown>,
  S = Record<string, unknown>,
> extends React.Component<WithWrapperProps<P>, S> {
  private static $injector: ng.IInjectorService;

  private readonly containerRef: React.RefObject<HTMLDivElement> =
    React.createRef();
  private $scope: IScope | undefined;

  constructor(props: P) {
    super(props);
    if (!ReactAngularWidget.$injector) {
      ReactAngularWidget.$injector = getAngularInjector();
    }
  }

  public componentDidMount() {
    const $compile = ReactAngularWidget.$injector.get('$compile');
    const $rootScope = ReactAngularWidget.$injector.get('$rootScope');
    this.$scope = $rootScope.$new(true);

    const { ngWrapperDivProps, ...props } = this.props;
    angular.extend(this.$scope, props);
    $compile(this.containerRef.current!)(this.$scope);
  }

  public componentDidUpdate() {
    this.$scope?.$applyAsync((scope) => {
      const { ngWrapperDivProps, ...props } = this.props;
      angular.extend(scope, props);
    });
  }

  public componentWillUnmount() {
    const scope = this.$scope;
    if (!scope || (scope as any).$$destroyed) {
      return;
    }

    // With React 18's concurrent rendering and automatic batching, componentWillUnmount
    // can be called during a batched update while Angular is in the middle of a digest cycle.
    // If we destroy the scope mid-digest, Angular's internal scope traversal will crash with
    // "Cannot read properties of null (reading '$$nextSibling')".
    // To avoid this, we defer scope destruction until after the current digest completes.
    const $rootScope = scope.$root as any;
    if ($rootScope?.$$phase) {
      // Digest in progress - defer destruction until after it completes
      $rootScope.$$postDigest(() => {
        if (!(scope as any).$$destroyed) {
          scope.$destroy();
        }
      });
    } else {
      scope.$destroy();
    }
  }

  public render() {
    const { ngWrapperDivProps } = this.props;
    return (
      <div
        {...ngWrapperDivProps}
        ref={this.containerRef}
        dangerouslySetInnerHTML={{ __html: this.html() }}
      />
    );
  }

  protected abstract html(): string;
}
